/**
 * MondayBoardSetup — White-Label → Support, for an organization with its OWN
 * board: the monday connection, then the board.
 *
 * Connect: "Connect monday.com" (OAuth, in a popup; the page watches for the
 * connection to appear) where this deployment has a monday app, or a pasted
 * API token. The page shows who is connected and how — never a token.
 *
 * The board: "Create a support board for me" (recommended) — a workspace, a
 * name built from the organization's own brand, private or shared — or "Use
 * an existing board" (its link, saved with the footer) with Check board /
 * Set up missing columns. A creation keeps its request id until it succeeds,
 * so pressing Create again after a failure continues the same board.
 */
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Button, Input, Popconfirm, Radio, Select, Tag } from 'antd';
import {
  ApiOutlined, CheckCircleFilled, ExclamationCircleFilled, ExportOutlined, LinkOutlined, ProjectOutlined,
} from '@ant-design/icons';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { bidiIsolate } from '@/utils/bidi';
import { safeHttpUrl } from '@/utils/safeHttpUrl';
import { type SupportView, boardNameWords, defaultBoardName } from './supportBoardModel';

const P = 'whiteLabel.supportBoard.monday';

type Path = 'create' | 'existing';

interface Props {
  tenantId: string;
  view: SupportView;
  brandName: string;
  /** The link and name fields of "Use an existing board" (saved with the footer). */
  existingFields: ReactNode;
  /** A new view from the server; `fromBoardChange` = the saved board changed. */
  onView: (view: SupportView, fromBoardChange: boolean) => void;
}

function newRequestId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-0000-4000-8000-${Math.random().toString(16).slice(2, 14).padEnd(12, '0')}`;
}

function errorCode(err: unknown): string {
  return (err as { response?: { data?: { error_code?: string } } })?.response?.data?.error_code || 'generic';
}

/** monday refused to CREATE the board (the connection itself is fine): the
 *  message says what to change, and "Use an existing board" is the way out. */
const CREATE_REFUSALS = new Set([
  'monday_private_not_on_plan', 'monday_workspace_closed', 'monday_create_not_allowed', 'monday_permission',
]);

interface Workspace { id: string; name: string; kind?: string }

export default function MondayBoardSetup({ tenantId, view, brandName, existingFields, onView }: Props) {
  const { t } = useLanguage();
  const uid = useId();
  const base = `/super-admin/tenants/${tenantId}/support-board`;
  const conn = view.monday.connection;
  const ownBoardSaved = view.mode === 'board' && !!view.own;

  const [tokenOpen, setTokenOpen] = useState(false);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [path, setPath] = useState<Path>(ownBoardSaved ? 'existing' : 'create');
  const [changingBoard, setChangingBoard] = useState(false);
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  /** Whether the account's plan has private boards: false = it has not (the
   *  option is disabled, with the reason); null = not known (nothing is). */
  const [privateBoards, setPrivateBoards] = useState<boolean | null>(null);
  const [workspaceId, setWorkspaceId] = useState<string | undefined>(undefined);
  const [boardName, setBoardName] = useState(() => defaultBoardName(boardNameWords(t), brandName, view.tenant_name));
  const [kind, setKind] = useState<'private' | 'public'>('private');
  const requests = useRef<{ real: string; test: string }>({ real: newRequestId(), test: newRequestId() });
  const watch = useRef<number | null>(null);
  /** What the last check added to the board (columns by key, groups by title). */
  const [added, setAdded] = useState<{ columns: string[]; groups: string[]; removed: string[] } | null>(null);
  const autoChecked = useRef<string | null>(null);

  useEffect(() => () => { if (watch.current) window.clearInterval(watch.current); }, []);

  // A board was saved (a pasted link, or one CorpBrain created): show it.
  const savedBoardId = view.mode === 'board' ? view.own?.board_id ?? null : null;
  useEffect(() => {
    if (savedBoardId) {
      setChangingBoard(false);
      setPath('existing');
    }
  }, [savedBoardId]);

  const choosing = conn && (!ownBoardSaved || changingBoard);
  useEffect(() => {
    if (!choosing || path !== 'create' || workspaces !== null) return;
    api.get(`${base}/monday/workspaces`)
      .then(res => {
        const list: Workspace[] = res.data?.workspaces || [];
        setWorkspaces(list);
        setWorkspaceId(current => current ?? (list.find(w => w.kind !== 'closed') ?? list[0])?.id);
        const privateOk: boolean | null = res.data?.private_boards ?? null;
        setPrivateBoards(privateOk);
        if (privateOk === false) setKind('public');
      })
      .catch(err => { setWorkspaces([]); setError(errorCode(err)); });
  }, [choosing, path, workspaces, base]);

  const run = async (label: string, call: () => Promise<{ data: SupportView }>, boardChange = false) => {
    setBusy(label);
    setError(null);
    try {
      const res = await call();
      onView(res.data, boardChange);
      return true;
    } catch (err) {
      setError(errorCode(err));
      return false;
    } finally {
      setBusy(null);
    }
  };

  // ── Connect ──
  const connectWithToken = () => run('token', () => api.post(`${base}/monday/token`, { token }))
    .then(ok => { if (ok) { setToken(''); setTokenOpen(false); } });

  const connectWithOAuth = async () => {
    setError(null);
    const popup = window.open('', 'corpbrain-monday-connect', 'popup,width=640,height=760');
    try {
      const res = await api.get(`${base}/monday/authorize`);
      if (popup) popup.location.href = res.data.authorization_url;
      else window.location.href = res.data.authorization_url;
    } catch (err) {
      popup?.close();
      setError(errorCode(err));
      return;
    }
    setBusy('oauth');
    const started = Date.now();
    if (watch.current) window.clearInterval(watch.current);
    watch.current = window.setInterval(async () => {
      const closed = !popup || popup.closed;
      try {
        const res = await api.get(base);
        if (res.data?.monday?.connection || closed || Date.now() - started > 5 * 60 * 1000) {
          if (watch.current) window.clearInterval(watch.current);
          watch.current = null;
          setBusy(null);
          onView(res.data, false);
        }
      } catch { /* the next tick asks again */ }
    }, 2000);
  };

  const disconnect = () => run('disconnect', () => api.delete(`${base}/monday`));

  // ── The board ──
  const create = async (test: boolean) => {
    const key = test ? 'test' : 'real';
    const name = test ? defaultBoardName(boardNameWords(t), brandName, view.tenant_name, true) : boardName.trim();
    const ok = await run(test ? 'create-test' : 'create', () => api.post(`${base}/monday/boards`, {
      request_id: requests.current[key], name, workspace_id: workspaceId ?? null, kind: test ? 'private' : kind,
    }), true);
    if (ok) {
      requests.current[key] = newRequestId();
      setChangingBoard(false);
      setPath('existing');
    }
  };
  // A check ADDS what a report needs (columns, the "Support reports" group)
  // and says what it added.
  const checking = (label: string, path_: string) => run(label, async () => {
    const res = await api.post(`${base}/${path_}`);
    const result = res.data?.setup_result;
    setAdded(result ? { columns: result.added_columns ?? [], groups: result.added_groups ?? [],
                        removed: result.removed_columns ?? [] } : null);
    return res;
  });
  const check = () => checking('check', 'check');
  const setup = () => checking('setup', 'setup-columns');

  const setupState = view.monday.board_setup;

  // A board saved with "Use an existing board" is set up at once — no extra
  // click: the first check adds the missing columns and the group.
  useEffect(() => {
    if (!conn || !ownBoardSaved || !savedBoardId || setupState?.checked_at) return;
    if (autoChecked.current === savedBoardId) return;
    autoChecked.current = savedBoardId;
    void check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conn, ownBoardSaved, savedBoardId, setupState?.checked_at]);
  const boardUrl = view.own?.board_url ? safeHttpUrl(view.own.board_url) : null;
  const boardLabel = setupState?.title || view.own?.board_name || (view.own ? `#${view.own.board_id}` : '');

  return (
    <div className="wl-sb-monday" data-testid="wl-support-board-monday">
      {/* ── The connection ── */}
      <div className="wl-sb-block">
        <span className="wl-sb-block-label">{t(`${P}.connectionTitle`)}</span>
        {conn ? (
          <div className="wl-sb-connection" data-testid="wl-support-board-connection">
            <span className="wl-sb-chip-mark" aria-hidden><ApiOutlined /></span>
            <span className="wl-sb-connection-text">
              <span className="wl-sb-connection-who">
                {t(`${P}.connectedAs`, {
                  user: bidiIsolate(conn.user_name || conn.user_email || ''),
                  account: bidiIsolate(conn.account_name || conn.account_slug || ''),
                })}
              </span>
              <span className="wl-field-hint">
                {conn.account_slug && <bdi dir="ltr">{conn.account_slug}.monday.com</bdi>}
              </span>
            </span>
            <Tag className="wl-sb-connection-kind">
              {conn.auth_kind === 'oauth' ? t(`${P}.viaOAuth`) : t(`${P}.viaToken`)}
            </Tag>
            <Popconfirm title={t(`${P}.disconnectConfirm`)} okText={t(`${P}.disconnect`)} cancelText={t('common.cancel')}
                        onConfirm={() => { void disconnect(); }}>
              <Button size="small" danger type="text" loading={busy === 'disconnect'}
                      data-testid="wl-support-board-disconnect">{t(`${P}.disconnect`)}</Button>
            </Popconfirm>
          </div>
        ) : (
          <div className="wl-sb-connect" data-testid="wl-support-board-connect">
            <p className="wl-field-hint">{t(`${P}.connectHint`)}</p>
            {view.monday.oauth_available && !tokenOpen && (
              <div className="wl-sb-actions">
                <Button type="primary" icon={<ApiOutlined />} onClick={() => { void connectWithOAuth(); }}
                        loading={busy === 'oauth'} data-testid="wl-support-board-oauth">
                  {t(`${P}.connect`)}
                </Button>
                <button type="button" className="wl-sb-textlink" onClick={() => setTokenOpen(true)}>
                  {t(`${P}.useToken`)}
                </button>
              </div>
            )}
            {(!view.monday.oauth_available || tokenOpen) && (
              <div className="wl-field">
                <label className="wl-field-label" htmlFor={`${uid}-token`}>{t(`${P}.tokenLabel`)}</label>
                <div className="wl-sb-inline">
                  <Input.Password id={`${uid}-token`} dir="ltr" value={token} autoComplete="off"
                                  onChange={e => setToken(e.target.value)} aria-describedby={`${uid}-token-hint`}
                                  data-testid="wl-support-board-token" />
                  <Button type="primary" disabled={!token.trim()} loading={busy === 'token'}
                          onClick={() => { void connectWithToken(); }} data-testid="wl-support-board-token-connect">
                    {t(`${P}.connectToken`)}
                  </Button>
                </div>
                <div className="wl-field-hint" id={`${uid}-token-hint`}>
                  {t(view.monday.oauth_available ? `${P}.tokenHint` : `${P}.tokenHintNoOAuth`)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── The board ── */}
      {conn && (
        <div className="wl-sb-block" data-testid="wl-support-board-setup">
          <span className="wl-sb-block-label">{t(`${P}.boardTitle`)}</span>
          {ownBoardSaved && !changingBoard && (
            <div className="wl-sb-boardcard" data-testid="wl-support-board-current">
              <div className="wl-sb-boardcard-head">
                <span className="wl-sb-chip-mark" aria-hidden><ProjectOutlined /></span>
                <bdi dir="auto" className="wl-sb-boardcard-name">{boardLabel}</bdi>
                {boardUrl && (
                  <a className="wl-sb-chip-open" href={boardUrl} target="_blank" rel="noopener noreferrer">
                    {t('whiteLabel.supportBoard.openBoard')} <ExportOutlined aria-hidden />
                  </a>
                )}
              </div>
              <div className="wl-sb-boardcard-state" data-testid="wl-support-board-columns">
                {!setupState?.checked_at && <span className="wl-field-hint">{t(`${P}.notChecked`)}</span>}
                {setupState?.missing && setupState.missing.length === 0 && (
                  <span className="wl-sb-link-state wl-sb-link-state--ok">
                    <CheckCircleFilled aria-hidden /> {t(`${P}.allColumns`)}
                  </span>
                )}
                {added && (added.columns.length > 0 || added.groups.length > 0 || added.removed.length > 0) && (
                  <span className="wl-sb-link-state wl-sb-link-state--ok" data-testid="wl-support-board-added">
                    <CheckCircleFilled aria-hidden />
                    {[
                      added.columns.length > 0 && t(`${P}.addedColumns`, {
                        columns: added.columns.map(key => t(`${P}.columns.${key}`)).join(', '),
                      }),
                      added.groups.length > 0 && t(`${P}.addedGroup`, { group: added.groups[0] }),
                      added.removed.length > 0 && t(`${P}.removedTicketStatus`),
                    ].filter(Boolean).join(' ')}
                  </span>
                )}
                {setupState?.missing && setupState.missing.length > 0 && (
                  <span className="wl-sb-link-state wl-sb-link-state--warn">
                    <ExclamationCircleFilled aria-hidden />
                    {t(`${P}.missingColumns`, {
                      columns: setupState.missing.map(key => t(`${P}.columns.${key}`)).join(', '),
                    })}
                  </span>
                )}
              </div>
              <div className="wl-sb-actions">
                <Button onClick={() => { void check(); }} loading={busy === 'check'}
                        data-testid="wl-support-board-check">{t(`${P}.check`)}</Button>
                {setupState?.missing && setupState.missing.length > 0 && (
                  <Button type="primary" onClick={() => { void setup(); }} loading={busy === 'setup'}
                          data-testid="wl-support-board-setup-columns">{t(`${P}.setupColumns`)}</Button>
                )}
                <button type="button" className="wl-sb-textlink" onClick={() => { setChangingBoard(true); setPath('create'); }}
                        data-testid="wl-support-board-change">{t(`${P}.useAnother`)}</button>
              </div>
            </div>
          )}

          {choosing && (
            <div className="wl-sb-boardchoice">
              <Radio.Group value={path} onChange={e => setPath(e.target.value)} optionType="button"
                           buttonStyle="solid" data-testid="wl-support-board-path">
                <Radio.Button value="create">{t(`${P}.pathCreate`)}</Radio.Button>
                <Radio.Button value="existing">{t(`${P}.pathExisting`)}</Radio.Button>
              </Radio.Group>
              {path === 'create' ? (
                <div className="wl-sb-board-fields" data-testid="wl-support-board-create">
                  <p className="wl-field-hint">{t(`${P}.createHint`)}</p>
                  <div className="wl-field">
                    <label className="wl-field-label" htmlFor={`${uid}-ws`}>{t(`${P}.workspace`)}</label>
                    <Select id={`${uid}-ws`} value={workspaceId} onChange={setWorkspaceId}
                            loading={workspaces === null} data-testid="wl-support-board-workspace"
                            options={(workspaces || []).map(w => ({
                              value: w.id,
                              label: (
                                <span>
                                  <bdi dir="auto">{w.name}</bdi>
                                  {w.kind === 'closed' && <span className="wl-sb-ws-kind"> · {t(`${P}.workspaceClosed`)}</span>}
                                </span>
                              ),
                            }))} />
                  </div>
                  <div className="wl-field">
                    <label className="wl-field-label" htmlFor={`${uid}-bname`}>{t(`${P}.newBoardName`)}</label>
                    <Input id={`${uid}-bname`} dir="auto" value={boardName} maxLength={255}
                           onChange={e => setBoardName(e.target.value)} data-testid="wl-support-board-new-name" />
                  </div>
                  <div className="wl-field">
                    <span className="wl-field-label" id={`${uid}-kind`}>{t(`${P}.visibility`)}</span>
                    <Radio.Group value={kind} onChange={e => setKind(e.target.value)} aria-labelledby={`${uid}-kind`}>
                      <Radio value="private" disabled={privateBoards === false}
                             aria-describedby={privateBoards === false ? `${uid}-noprivate` : undefined}
                             data-testid="wl-support-board-kind-private">{t(`${P}.private`)}</Radio>
                      <Radio value="public">{t(`${P}.public`)}</Radio>
                    </Radio.Group>
                    {privateBoards === false ? (
                      <div className="wl-field-hint" id={`${uid}-noprivate`} data-testid="wl-support-board-no-private">
                        {t(`${P}.privateNotOnPlan`)}
                      </div>
                    ) : (
                      <div className="wl-field-hint">{t(kind === 'private' ? `${P}.privateHint` : `${P}.publicHint`)}</div>
                    )}
                  </div>
                  <div className="wl-sb-actions">
                    <Button type="primary" icon={<ProjectOutlined />} disabled={!boardName.trim()}
                            loading={busy === 'create'} onClick={() => { void create(false); }}
                            data-testid="wl-support-board-create-button">{t(`${P}.createButton`)}</Button>
                    <button type="button" className="wl-sb-textlink" disabled={busy !== null}
                            onClick={() => { void create(true); }} data-testid="wl-support-board-create-test">
                      {t(`${P}.createTest`)}
                    </button>
                    {changingBoard && (
                      <button type="button" className="wl-sb-textlink" onClick={() => setChangingBoard(false)}>
                        {t('common.cancel')}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div data-testid="wl-support-board-existing">
                  {existingFields}
                  <p className="wl-field-hint"><LinkOutlined aria-hidden /> {t(`${P}.existingHint`)}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="wl-sb-link-state wl-sb-link-state--error" role="alert" data-testid="wl-support-board-monday-error">
          <ExclamationCircleFilled aria-hidden />
          <span>{t(`${P}.errors.${error}`, { defaultValue: t(`${P}.errors.generic`) })}</span>
          {CREATE_REFUSALS.has(error) && path === 'create' && (
            <button type="button" className="wl-sb-textlink" data-testid="wl-support-board-use-existing"
                    onClick={() => { setError(null); setPath('existing'); }}>
              {t(`${P}.useExistingInstead`)}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
